import _ from 'lodash';

Mobi2Go._class = Mobi2Go._class || {};

/**-----------------------------------------------------------------------------
 */
var OrderPaymentProcessor = (Mobi2Go._class.OrderPaymentProcessor = function(
    processor,
    order
) {
    const available_payment_processors = order.getAvailablePaymentProcessors();
    processor = _.find(order.getAvailablePaymentProcessors(), {
        name: processor.name,
    });

    if (!processor && available_payment_processors.length) {
        processor = available_payment_processors[0];
    } else if (!available_payment_processors.length) {
        return null;
    }

    return OrderPaymentProcessor[processor.form_type](processor, order);
});

/**-----------------------------------------------------------------------------
 * Base Payment Processor
 */
function BasePaymentProcessor(properties, order) {
    this.init(properties, order);
}

_.extend(BasePaymentProcessor.prototype, {
    init(properties, order) {
        this.status = 'unconfirmed';
        this.message = 'transaction uninitiated';
        this.last_error = '';
        this.last_transaction_id = null;
        this.should_show_billing_details = false;
        this._properties = null;

        try {
            if (
                localStorage &&
                localStorage.getItem('Mobi2GoLastPaymentError')
            ) {
                this.last_error = localStorage.getItem(
                    'Mobi2GoLastPaymentError'
                );
            }

            if (
                localStorage &&
                localStorage.getItem('Mobi2GoLastPaymentTransactionId')
            ) {
                this.last_transaction_id = localStorage.getItem(
                    'Mobi2GoLastPaymentTransactionId'
                );
            }
        } catch (e) {}

        this.setProperties(properties);

        this.order = order || Mobi2Go.Order;

        this.preloadLogoImage();
    },
    setProperties(properties) {
        this.name = properties.name;
        this.storefront_display_name = properties.storefront_display_name;
        this.form_type = properties.form_type;
        this.is_card_tokenisation_available =
            properties.is_card_tokenisation_available;
        this._properties = properties;
    },
    getTransaction() {
        return {
            status: this.status,
            message: this.message,
        };
    },
    setTransaction(transaction) {
        if (this.status !== transaction.status) {
            this.status = transaction.status;
            this.order.trigger('updatePaymentProcessorStatus', this.status);
        }

        const current_error_signiture =
            this.last_error + this.last_transaction_id;
        const new_error_signiture =
            transaction.last_error + transaction.last_transaction_id;

        try {
            if (this.last_error !== transaction.last_error) {
                this.last_error = transaction.last_error;
                // Save in localStore to prevent message showing twice on refresh
                if (localStorage) {
                    localStorage.setItem(
                        'Mobi2GoLastPaymentError',
                        this.last_error
                    );
                }
            }

            if (this.last_transaction_id !== transaction.last_transaction_id) {
                this.last_transaction_id = transaction.last_transaction_id;
                // Save in localStore to prevent message showing twice on refresh
                if (localStorage) {
                    localStorage.setItem(
                        'Mobi2GoLastPaymentTransactionId',
                        this.last_transaction_id
                    );
                }
            }
        } catch (e) {}

        if (
            current_error_signiture !== new_error_signiture &&
            this.last_error
        ) {
            this.order.trigger('updatePaymentProcessorError', this.last_error);
        }

        if (this.message !== transaction.message) {
            this.message = transaction.message;
            this.order.trigger('updatePaymentProcessorMessage', this.message);

            // Updated payment processor system no longer expects
            // error messages in the 'message' field. So this should be
            // removed shortly.
            const error = this.getError();
            if (error && this.last_error) {
                this.order.trigger('updatePaymentProcessorError', error);
            }
        }
    },
    getStatus() {
        return this.status;
    },
    isUnconfirmed() {
        return this.status === 'unconfirmed';
    },
    isConfirming() {
        return this.status === 'confirming';
    },
    isConfirmed() {
        return this.status === 'confirmed';
    },
    getMessage() {
        return this.message;
    },
    getError() {
        const label = 'error: ';
        if (this.message.substr(0, label.length) == label) {
            return this.message.substr(label.length);
        }
        return null;
    },
    getLastError() {
        return this.last_error;
    },
    getLogoUrl(is_dark_background) {
        return `${
            Mobi2Go.app.base_url
        }/store/application/v3/dist/img/payment-processor-logos/${this.name}${
            is_dark_background ? '--light' : ''
        }.png`;
    },
    preloadLogoImage() {
        if (!this.preloadLogoImage.loaded) {
            this.preloadLogoImage.loaded = {};
        }
        if (this.preloadLogoImage.loaded[this.name]) {
            return;
        }
        this.preloadLogoImage.loaded[this.name] = true;

        const that = this;
        _.delay(function() {
            const image_light = new Image();
            image_light.src = that.getLogoUrl();
            const image_dark = new Image();
            image_dark.src = that.getLogoUrl(true);
        }, 0);
    },
    getFormType() {
        return this.form_type;
    },
    getName() {
        return this.name;
    },
    shouldShowBillingDetails() {
        return !!this.should_show_billing_details;
    },
    detachFromOrder() {},
    attachToOrder() {},
});

/**-----------------------------------------------------------------------------
 * Processor Form Types
 */
_.extend(OrderPaymentProcessor, {
    /**-------------------------------------------------------------------------
     * Direct Payment Form
     *
     * Credit card details are collected on a local form which POSTS to
     * mobi2go.com.
     */
    direct(properties, order) {
        const DirectProcessor = new BasePaymentProcessor(properties, order);

        let credit_card = {
            number: null,
            card_name: null,
            expiry_month: null,
            expiry_year: null,
            cvv: null,
            billing_details: {
                first_name: null,
                last_name: null,
                address_1: null,
                address_2: null,
                country: null,
                city: null,
                region: null,
                post_code: null,
            },
        };

        DirectProcessor.setCreditCard = function(new_credit_card) {
            credit_card = new_credit_card;
            order.trigger('updateCreditCard', credit_card);

            return DirectProcessor;
        };

        DirectProcessor.isCreditCardValid = function() {
            return (
                DirectProcessor.isCreditCardNumberValid() &&
                DirectProcessor.isCreditCardExpiryValid()
            );
        };

        DirectProcessor.isCreditCardNumberValid = function() {
            return credit_card.number && `${credit_card.number}`.length > 10;
        };

        DirectProcessor.isCreditCardExpiryValid = function() {
            return !!credit_card.expiry_month && !!credit_card.expiry_year;
        };

        DirectProcessor.confirm = function(callback) {
            if (!DirectProcessor.isConfirming()) {
                throw new Error('Cannot confirm payment, not confirming');
            }

            serverRequest(credit_card, callback);

            return DirectProcessor;
        };

        DirectProcessor.isBillingDetailsValid = function() {
            if (!this.shouldShowBillingDetails()) {
                return true;
            }

            return (
                !!credit_card.billing_details.first_name &&
                !!credit_card.billing_details.last_name &&
                !!credit_card.billing_details.address_1 &&
                !!credit_card.billing_details.city &&
                !!credit_card.billing_details.country &&
                !!credit_card.billing_details.post_code &&
                !!credit_card.billing_details.region
            );
        };

        DirectProcessor.attachToOrder = function(order) {
            order.bind('updateProgressPhase', onConfirming);
            order.bind('updatePaymentProcessorError', onError);
            order.bind('confirmClicked', onConfirmClicked);
        };

        DirectProcessor.detachFromOrder = function(order) {
            order.unbind('updateProgressPhase', onConfirming);
            order.unbind('updatePaymentProcessorError', onError);
            order.unbind('confirmClicked', onConfirmClicked);
        };

        var onConfirmClicked = function onConfirmClicked() {
            // ApplePay needs to be triggered by an user gesture event
            if (DirectProcessor.confirmClicked) {
                DirectProcessor.confirmClicked();
            }
        };

        var onConfirming = function onConfirming(phase) {
            if (order.getPaymentMethod() !== 'online') {
                return;
            }

            if (phase === 'confirming') {
                // We need to ensure that the order has been saved to the server
                // because the backend may need to do setup in 'confirming'
                // phase before being able to accept a payment
                order.save(function() {
                    if (DirectProcessor.isCreditCardValid()) {
                        DirectProcessor.confirm();
                    } else {
                        order.trigger('invalidCreditCardWarning');
                        order.cancelConfirmation();
                    }
                });
            }
        };

        // Auto-unconfirm on error
        var onError = function onError(error) {
            if (error) {
                // order.progress will be set after payment processor error,
                // so cancelling confirmation must be deferred to the end of the
                // callstack
                _.defer(order.cancelConfirmation);
            }
        };

        /**---------------------------------------------------------------------
         */
        function serverRequest(data, callback) {
            order.trigger('paymentRequest');
            Mobi2Go.app.serverRequest({
                api_version: 1,
                endpoint: ['orders', order.getId(), 'direct_payment'],
                data,
                type: 'POST',
                // Payment endpoint returns order object to save from having
                // to do an extra request to fetch order state
                success(response) {
                    order
                        .set(response)
                        .trigger('syncSuccessFromPayment', response);
                    const transaction = DirectProcessor.getTransaction();
                    order.trigger('paymentResponse', transaction);

                    if (_.isFunction(callback)) {
                        callback(transaction);
                    }
                },
            });
        }

        /**---------------------------------------------------------------------
         * Braintree - use client encryption lib to encrypt payment details
         * before sending to the server
         */
        if (properties.name === 'Braintree') {
            let encryption_client = null;
            let cse_key = null; // Client side encryption key

            if (DirectProcessor.shouldShowBillingDetails()) {
                credit_card.billing_details = {
                    first_name: null,
                    last_name: null,
                    address_1: null,
                    address_2: null,
                    country: null,
                    city: null,
                    region: null,
                    post_code: null,
                };
            }

            var loadExternalLib = function(callback) {
                if (window.Braintree) {
                    if (_.isFunction(callback)) {
                        _.defer(callback);
                    }
                    return;
                }

                const src = 'https://js.braintreegateway.com/v1/braintree.js';
                const is_external = true;
                Mobi2Go.helpers.loadScripts([src], is_external, callback);
            };
            const initateEncryptionClient = function() {
                if (
                    !encryption_client ||
                    encryption_client.publicKey !== cse_key
                ) {
                    encryption_client = window.Braintree.create(cse_key);
                }
            };

            loadExternalLib();

            DirectProcessor.setTransaction = function(transaction) {
                BasePaymentProcessor.prototype.setTransaction.call(
                    DirectProcessor,
                    transaction
                );
                cse_key = transaction.cse_key;

                this.should_show_billing_details =
                    transaction.should_show_billing_details;

                return DirectProcessor;
            };

            // Override
            DirectProcessor.confirm = function(callback) {
                loadExternalLib(function() {
                    initateEncryptionClient();
                    let encrypted_data = null;

                    if (!DirectProcessor.shouldShowBillingDetails()) {
                        encrypted_data = {
                            number: encryption_client.encrypt(
                                credit_card.number
                            ),
                            expiry_month: encryption_client.encrypt(
                                credit_card.expiry_month
                            ),
                            expiry_year: encryption_client.encrypt(
                                credit_card.expiry_year
                            ),
                            cvc: encryption_client.encrypt(credit_card.cvc),
                        };
                    } else {
                        encrypted_data = {};

                        _.forIn(credit_card, function(value, key) {
                            if (key !== 'billing_details') {
                                encrypted_data[key] = encryption_client.encrypt(
                                    value
                                );
                            } else {
                                encrypted_data[key] = {};

                                _.forIn(value, function(v, k) {
                                    encrypted_data[key][k] = v;
                                });
                            }
                        });
                    }
                    serverRequest(encrypted_data, callback);
                });

                return DirectProcessor;
            };
        }

        /**---------------------------------------------------------------------
         * Stripe - create token on client side so no sensitive info hits the
         * server
         */
        if (
            properties.name === 'Stripe' ||
            properties.name === 'StripeCustomConnect' ||
            properties.name === 'Mobi2GoPayments'
        ) {
            DirectProcessor.setTransaction = function(transaction) {
                BasePaymentProcessor.prototype.setTransaction.call(
                    DirectProcessor,
                    transaction
                );
                this.should_show_billing_details =
                    transaction.should_show_billing_details;
                if (
                    transaction &&
                    transaction.metadata &&
                    'requires_action' in transaction.metadata
                ) {
                    this.requires_action = transaction.metadata.requires_action;
                }
                return DirectProcessor;
            };

            DirectProcessor.isCreditCardNumberValid = function() {
                if (this.__stripe_form) {
                    return this.__stripe_form.isCreditCardNumberValid();
                }
                return false;
            };

            DirectProcessor.isCreditCardExpiryValid = function() {
                if (this.__stripe_form) {
                    return this.__stripe_form.isCreditCardExpiryValid();
                }
                return false;
            };

            DirectProcessor.isExtraDetailsValid = function() {
                if (this.__stripe_form) {
                    return this.__stripe_form.isExtraDetailsValid();
                }
                return false;
            };

            DirectProcessor.isBillingDetailsValid = function() {
                return this.isExtraDetailsValid();
            };

            DirectProcessor.isCreditCardValid = function() {
                if (Mobi2Go.Order.getPayment().card_token_id) {
                    return true;
                }
                return (
                    this.isCreditCardNumberValid() &&
                    this.isCreditCardExpiryValid() &&
                    this.isExtraDetailsValid()
                );
            };

            var onConfirmClicked = function onConfirmClicked() {
                if (order.getPaymentMethod() !== 'online') {
                    return;
                }
                order.save(function() {
                    if (DirectProcessor.isCreditCardValid()) {
                        DirectProcessor.confirm();
                    } else {
                        order.trigger('invalidCreditCardWarning');
                        order.cancelConfirmation();
                    }
                });
            };

            // override
            DirectProcessor.attachToOrder = function(order) {
                order.bind('updatePaymentProcessorError', onError);
                order.bind('confirmClicked', onConfirmClicked);
            };

            // override
            DirectProcessor.detachFromOrder = function(order) {
                order.unbind('updatePaymentProcessorError', onError);
                order.unbind('confirmClicked', onConfirmClicked);
            };

            // override
            DirectProcessor.confirm = function(callback) {
                function onActionHandled(err, payment_intent) {
                    const request = {
                        requires_intent_confirmation: true,
                    };
                    if (payment_intent?.id) {
                        request.payment_intent_id = payment_intent.id;
                    } else if (err) {
                        if (err.payment_intent?.id) {
                            request.payment_intent_id = err.payment_intent.id;
                        }
                        if (err.type) {
                            request.error_type = err.type;
                        }
                        if (err.code) {
                            request.error_code = err.code;
                        }
                    }
                    serverRequest(request);
                    if (Mobi2Go.UI.stickyAside) {
                        /**
                         * Stripe.js may alter the layout and move the order side section.
                         * We'll trigger an update to reposition that element once
                         * Stripe.js action is completed.
                         */
                        const force = true;
                        Mobi2Go.UI.stickyAside.updateDimensions(force);
                    }
                }

                function handleServerResponse(transaction) {
                    if ('requires_action' in this) {
                        if (this.__stripe_form) {
                            this.__stripe_form.handleAction(
                                this.requires_action,
                                onActionHandled.bind(this)
                            );
                        }
                    }
                    if (_.isFunction(callback)) {
                        callback(transaction);
                    }
                }

                function onPaymentMethodCreated(err, result) {
                    const stripe_data = {
                        requires_intent: true,
                    };
                    if (result) {
                        stripe_data.payment_method = result.payment_method.id;
                        stripe_data.brand = result.payment_method.card.brand;
                        stripe_data.country =
                            result.payment_method.card.country;
                    }
                    serverRequest(stripe_data, handleServerResponse.bind(this));
                }

                if (!Mobi2Go.Order.getPayment().card_token_id) {
                    if (this.__stripe_form) {
                        this.__stripe_form.createPaymentMethod(
                            onPaymentMethodCreated.bind(this)
                        );
                    }
                } else {
                    serverRequest({}, handleServerResponse.bind(this));
                }

                return DirectProcessor;
            };
        }

        /**---------------------------------------------------------------------
         * Google Pay
         */
        if (properties.name === 'Mobi2GoGooglePay') {
            var loadExternalLib = function(callback) {
                if (window.Stripe && window.Stripe.StripeV3) {
                    if (_.isFunction(callback)) {
                        _.defer(callback);
                    }
                    return;
                }

                const src = [
                    'https://js.stripe.com/v2/',
                    'https://js.stripe.com/v3/',
                ];
                const is_external = true;
                Mobi2Go.helpers.loadScripts(src, is_external, callback);

                if (!window.googlePayHelper) {
                    window.googlePayHelper = {
                        id: 0,
                        callbacks: {},
                        callback(callback) {
                            const callbackId = window.googlePayHelper.id++;
                            window.googlePayHelper.callbacks[
                                callbackId
                            ] = function() {
                                delete window.googlePayHelper.callbacks[
                                    callbackId
                                ];
                                callback.apply(null, arguments);
                            };
                            return `window.googlePayHelper.callbacks[${callbackId}]`;
                        },
                    };
                }
            };

            const generatePaymentRequestOptions = function generatePaymentRequestOptions() {
                return {
                    country: Mobi2Go.headoffice.country_iso3166_alpha2,
                    currency: Mobi2Go.headoffice.currency_abrv.toLowerCase(),
                    total: {
                        label: order.getLocation().name,
                        amount: Math.round(
                            order.getPaymentTotal().toFloat() * 100
                        ),
                    },
                    displayItems: order.getProducts().map(function(product) {
                        return {
                            label: `${product.quantity} × ${
                                product.menu_product.name
                            }${product.size ? `(${product.size})` : ''}`,
                            amount: Math.round(
                                product.list_total.toFloat() * 100
                            ),
                        };
                    }),
                    requestPayerName: true,
                    requestPayerEmail: true,
                    requestPayerPhone: true,
                };
            };

            const onActionHandled = function onActionHandled(
                err,
                payment_intent
            ) {
                const request = {
                    requires_intent_confirmation: true,
                };
                if (payment_intent?.id) {
                    request.payment_intent_id = payment_intent.id;
                } else if (err) {
                    if (err.payment_intent?.id) {
                        request.payment_intent_id = err.payment_intent.id;
                    }
                    if (err.type) {
                        request.error_type = err.type;
                    }
                    if (err.code) {
                        request.error_code = err.code;
                    }
                }
                serverRequest(request);
                if (Mobi2Go.UI.stickyAside) {
                    /**
                     * Stripe.js may alter the layout and move the order side section.
                     * We'll trigger an update to reposition that element once
                     * Stripe.js action is completed.
                     */
                    const force = true;
                    Mobi2Go.UI.stickyAside.updateDimensions(force);
                }
            };

            const onPaymentMethodCreated = function onPaymentMethodCreated(
                stripe,
                result
            ) {
                const stripe_data = {
                    requires_intent: true,
                };
                if (result) {
                    stripe_data.payment_method = result.paymentMethod.id;
                    stripe_data.brand = result.paymentMethod.card.brand;
                    stripe_data.country = result.paymentMethod.card.country;
                    stripe_data.email_address = result.payerEmail;
                    stripe_data.name = result.payerName;
                    stripe_data.phone_number = result.payerPhone;
                }
                const processor = this;
                serverRequest(stripe_data, function() {
                    result.complete('success');
                    if ('requires_action' in processor) {
                        if (stripe) {
                            stripe
                                .handleCardAction(processor.requires_action)
                                .then(function(result) {
                                    onActionHandled.call(
                                        processor,
                                        result.error,
                                        result.paymentIntent
                                    );
                                })
                                .catch(function(error) {
                                    onActionHandled.call(processor, error);
                                });
                        }
                    }
                });
            };

            const onPaymentRequestCancel = function onPaymentRequestCancel() {
                order.cancelConfirmation();
            };

            const onStripeLoaded = function onStripeLoaded() {
                if (window.googlePayAvailable !== undefined) {
                    return;
                }
                window.googlePayAvailable = true;
                const stripe = window.Stripe(properties.publishable_key);
                const paymentRequest = stripe.paymentRequest(
                    generatePaymentRequestOptions()
                );
                paymentRequest.canMakePayment().then(function(result) {
                    window.googlePayAvailable = !!(result && !result.applePay);
                    if (
                        window.googlePayBridge &&
                        window.googlePayBridge.canMakePayment
                    ) {
                        window.googlePayBridge.canMakePayment(
                            window.googlePayHelper.callback(function(
                                nativeAvailable
                            ) {
                                window.googlePayAvailable = !!nativeAvailable;
                                if (window.googlePayAvailable) {
                                    window.googlePayNative = true;
                                }
                                order.setAvailablePaymentProcessors(
                                    order
                                        .getAvailablePaymentProcessors()
                                        .slice()
                                );
                            })
                        );
                    } else {
                        order.setAvailablePaymentProcessors(
                            order.getAvailablePaymentProcessors().slice()
                        );
                    }
                });
            };

            loadExternalLib(onStripeLoaded);

            var startNativePayment = function startNativePayment() {
                const stripe = window.Stripe(properties.publishable_key);
                const processor = DirectProcessor;
                window.googlePayBridge.payWithGooglePay(
                    JSON.stringify({
                        publishable_key: properties.publishable_key,
                        order: generatePaymentRequestOptions(),
                    }),
                    window.googlePayHelper.callback(function(stripe_data) {
                        try {
                            if (stripe_data) {
                                serverRequest(stripe_data, function() {
                                    if ('requires_action' in processor) {
                                        if (stripe) {
                                            stripe
                                                .handleCardAction(
                                                    processor.requires_action
                                                )
                                                .then(function(result) {
                                                    onActionHandled.call(
                                                        processor,
                                                        result.error,
                                                        result.paymentIntent
                                                    );
                                                })
                                                .catch(function(error) {
                                                    onActionHandled.call(
                                                        processor,
                                                        error
                                                    );
                                                });
                                        }
                                    }
                                });
                            } else {
                                order.cancelConfirmation();
                            }
                        } catch (e) {
                            order.cancelConfirmation();
                        }
                    })
                );
            };

            var startWebPayment = function startWebPayment() {
                const stripe = window.Stripe(properties.publishable_key);
                const paymentRequest = stripe.paymentRequest(
                    generatePaymentRequestOptions()
                );
                paymentRequest.canMakePayment().then(function(result) {
                    if (result) {
                        paymentRequest
                            .on(
                                'paymentmethod',
                                onPaymentMethodCreated.bind(
                                    DirectProcessor,
                                    stripe
                                )
                            )
                            .on('cancel', onPaymentRequestCancel);
                        paymentRequest.show();
                    } else {
                        // Somehow google pay is not available anymore
                        order.cancelConfirmation();
                    }
                });
            };

            var isNative = function isNative() {
                return (
                    window.googlePayNative &&
                    window.googlePayBridge &&
                    window.googlePayBridge.payWithGooglePay
                );
            };

            var onConfirmClicked = function onConfirmClicked() {
                if (
                    order.getPaymentMethod() !== 'online' ||
                    order.getPaymentTotal().toFloat() === 0.0
                ) {
                    return;
                }

                if (isNative()) {
                    startNativePayment();
                } else {
                    startWebPayment();
                }
            };

            DirectProcessor.should_show_billing_details = false;

            DirectProcessor.isCreditCardValid = function() {
                return true;
            };

            DirectProcessor.setTransaction = function(transaction) {
                BasePaymentProcessor.prototype.setTransaction.call(
                    DirectProcessor,
                    transaction
                );
                if (
                    transaction &&
                    transaction.metadata &&
                    'requires_action' in transaction.metadata
                ) {
                    this.requires_action = transaction.metadata.requires_action;
                }
                return DirectProcessor;
            };

            // Override
            DirectProcessor.confirm = function(callback) {
                return DirectProcessor;
            };

            // override
            DirectProcessor.attachToOrder = function(order) {
                order.bind('updatePaymentProcessorError', onError);
                order.bind('confirmClicked', onConfirmClicked);
            };

            // override
            DirectProcessor.detachFromOrder = function(order) {
                order.unbind('updatePaymentProcessorError', onError);
                order.unbind('confirmClicked', onConfirmClicked);
            };
        }

        /**---------------------------------------------------------------------
         * Apple Pay
         */
        if (properties.name === 'Mobi2GoApplePay') {
            const publishable_key = null;

            if (properties.publishable_key && window.Stripe) {
                window.Stripe.setPublishableKey(properties.publishable_key);
            }
            if (properties.stripe_account && window.Stripe) {
                if (
                    properties.stripe_account !==
                    window.Stripe.applePay.stripeAccount
                ) {
                    /* Stripe account changed, we need to check again if available */
                    window.applePayAvailable = undefined;
                }
                window.Stripe.applePay.stripeAccount =
                    properties.stripe_account;
            }

            var loadExternalLib = function(callback) {
                if (window.Stripe) {
                    if (_.isFunction(callback)) {
                        _.defer(callback);
                    }
                    return;
                }

                const src = 'https://js.stripe.com/v2/';
                const is_external = true;
                Mobi2Go.helpers.loadScripts([src], is_external, callback);
            };

            DirectProcessor.isCreditCardValid = function() {
                return true;
            };

            DirectProcessor.should_show_billing_details = false;

            loadExternalLib(function() {
                if (window.applePayAvailable !== undefined) {
                    return;
                }
                window.Stripe.setPublishableKey(properties.publishable_key);
                window.Stripe.applePay.stripeAccount =
                    properties.stripe_account;
                window.Stripe.applePay.checkAvailability(function(available) {
                    window.applePayAvailable = !!available;
                    if (
                        window.ApplePayBridge &&
                        window.ApplePayBridge.canMakePayments
                    ) {
                        window.ApplePayBridge.canMakePayments(
                            { publishableKey: properties.publishable_key },
                            function(nativeAvailable) {
                                window.applePayAvailable = !!nativeAvailable;
                                if (window.applePayAvailable) {
                                    window.applePayNative = true;
                                }
                                order.setAvailablePaymentProcessors(
                                    order
                                        .getAvailablePaymentProcessors()
                                        .slice()
                                );
                            }
                        );
                    } else {
                        order.setAvailablePaymentProcessors(
                            order.getAvailablePaymentProcessors().slice()
                        );
                    }
                });
            });

            const getLineItems = function getLineItems() {
                const line_items = [];
                if (!order.getTip().isEqual(0)) {
                    line_items.push({
                        type: 'final',
                        label: 'Tip',
                        amount: order
                            .getTip()
                            .toFloat()
                            .toFixed(2),
                    });
                }
                if (!order.getSurcharge().isEqual(0)) {
                    line_items.push({
                        type: 'final',
                        label: 'Surcharge',
                        amount: order
                            .getSurcharge()
                            .toFloat()
                            .toFixed(2),
                    });
                }
                if (!order.getDiscountsTotal().isEqual(0)) {
                    line_items.push({
                        type: 'final',
                        label: 'Discount',
                        amount: order
                            .getDiscountsTotal()
                            .toFloat()
                            .toFixed(2),
                    });
                }
                line_items.push({
                    type: 'final',
                    label: 'Subtotal',
                    amount: (
                        order.getPaymentTotal().toFloat() -
                        order.getSalesTaxTotal().toFloat()
                    ).toFixed(2),
                });
                line_items.push({
                    type: 'final',
                    label: 'Tax',
                    amount: order
                        .getSalesTaxTotal()
                        .toFloat()
                        .toFixed(2),
                });

                return line_items;
            };

            var startNativePayment = function startNativePayment() {
                const request = {
                    countryCode: Mobi2Go.headoffice.country_iso3166_alpha2,
                    currencyCode: Mobi2Go.headoffice.currency_abrv,
                    total: order
                        .getPaymentTotal()
                        .toFloat()
                        .toFixed(2),
                    companyName: order.getLocation().name,
                    lineItems: getLineItems(),
                    publishableKey: properties.publishable_key,
                };
                window.ApplePayBridge.payWithApplePay(request, function(
                    stripe_data
                ) {
                    if (!stripe_data) {
                        order.cancelConfirmation();
                    } else {
                        serverRequest(stripe_data, function() {
                            window.ApplePayBridge.completion();
                        });
                    }
                });
            };

            var startWebPayment = function startWebPayment() {
                const request = {
                    countryCode: Mobi2Go.headoffice.country_iso3166_alpha2,
                    currencyCode: Mobi2Go.headoffice.currency_abrv,
                    lineItems: getLineItems(),
                    total: {
                        label: order.getLocation().name,
                        amount: order
                            .getPaymentTotal()
                            .toFloat()
                            .toFixed(2),
                    },
                    requiredShippingContactFields: ['phone', 'email', 'name'],
                };
                const session = window.Stripe.applePay.buildSession(
                    request,
                    function(result, completion) {
                        const stripe_data = {
                            token: result.token.id,
                            brand: result.token.card.brand,
                            country: result.token.card.country,
                            email_address: result.shippingContact.emailAddress,
                            name: `${result.shippingContact.givenName} ${result.shippingContact.familyName}`,
                            phone_number: result.shippingContact.phoneNumber,
                        };

                        serverRequest(stripe_data, function() {
                            completion(ApplePaySession.STATUS_SUCCESS);
                        });
                    },
                    function(error) {
                        order.cancelConfirmation();
                    }
                );

                session.oncancel = function() {
                    order.cancelConfirmation();
                };

                session.begin();
            };

            var isNative = function isNative() {
                return (
                    window.applePayNative &&
                    window.ApplePayBridge &&
                    window.ApplePayBridge.payWithApplePay
                );
            };

            DirectProcessor.confirmClicked = function() {
                if (
                    order.getPaymentMethod() === 'online' &&
                    order.getPaymentTotal().toFloat()
                ) {
                    if (isNative()) {
                        startNativePayment();
                    } else {
                        startWebPayment();
                    }
                }

                return DirectProcessor;
            };

            // Override
            DirectProcessor.confirm = function(callback) {
                return DirectProcessor;
            };
        }

        /**---------------------------------------------------------------------
         * Aston Club
         */
        if (properties.name === 'AstonClub') {
            var loadExternalLib = function(callback) {
                if (window.Tidy) {
                    if (_.isFunction(callback)) {
                        _.defer(callback);
                    }
                    return;
                }

                const sandbox =
                    'https://static.mytidyapp.com/tidywebsdk/current/sandbox.js';
                const src =
                    'https://static.mytidyapp.com/tidywebsdk/current/tidy.min.js';
                let scripts = [src];
                const is_external = true;

                if (properties.sandbox_enabled) {
                    scripts = [sandbox, src];
                }

                Mobi2Go.helpers.loadScripts(scripts, is_external, callback);
            };

            DirectProcessor.isCreditCardValid = function() {
                return true;
            };

            DirectProcessor.should_show_billing_details = false;

            loadExternalLib();

            // Override
            DirectProcessor.confirm = function(callback) {
                loadExternalLib(function() {
                    const tidy = new Tidy({ clientId: properties.client_id });
                    const items = _.map(
                        DirectProcessor.order.getProducts(),
                        function(item) {
                            return {
                                name: item.menu_product.name,
                                quantity: item.quantity,
                                amount: item.total[0] * 100,
                            };
                        }
                    );
                    const order = {
                        merchantId: properties.merchant_id,
                        orderRef: DirectProcessor.order.getId(),
                        total: DirectProcessor.order.getPaymentTotal()[0] * 100,
                        tax: DirectProcessor.order.getSalesTaxTotal()[0] * 100,
                        items,
                    };

                    tidy.payment(order, function(err, native_data) {
                        if (err) {
                            DirectProcessor.order.trigger(
                                'updateProgressError',
                                err
                            );
                            DirectProcessor.order.trigger(
                                'updatePaymentProcessorError',
                                err.message
                            );
                            return false;
                        }
                        const aston_data = {
                            nonce: native_data.nonce,
                        };
                        serverRequest(aston_data, function(transaction) {
                            if (_.isFunction(callback)) {
                                callback(transaction);
                            }
                            tidy.complete('Thank you for ordering');
                        });
                    });
                });

                return DirectProcessor;
            };
        }

        /**---------------------------------------------------------------------
         * Group Tab - no credit card number, just a name for the payment
         */
        if (properties.name === 'GroupTab') {
            let payment_name = null;

            DirectProcessor.setPaymentName = function(new_payment_name) {
                payment_name = new_payment_name;
                order.trigger('updatePaymentName', new_payment_name);

                return DirectProcessor;
            };

            DirectProcessor.isCreditCardValid = function() {
                return !!payment_name;
            };

            DirectProcessor.confirm = function(callback) {
                if (!DirectProcessor.isConfirming()) {
                    throw new Error('Cannot confirm payment, not confirming');
                }

                serverRequest({name: payment_name}, callback);

                return DirectProcessor;
            };
        }

        if (
            properties.name === 'AdyenTerminal' ||
            properties.name === 'WindcaveTerminal' ||
            properties.name === 'SquareTerminal'
        ) {
            DirectProcessor.isCreditCardValid = function() {
                return true;
            };

            DirectProcessor.should_show_billing_details = false;

            const POLL_INTERVAL = 1000;
            let interval = null;
            const startLoadInterval = function startLoadInterval() {
                if (
                    order.getProgressPhase() === 'confirming' &&
                    order.getProgressStatus() === 'pending payment'
                ) {
                    if (!interval) {
                        interval = setInterval(order.load, POLL_INTERVAL);
                    }
                } else {
                    stopLoadInterval();
                }
            };

            var stopLoadInterval = function stopLoadInterval() {
                clearTimeout(interval);
                interval = null;
            };

            const onUpdateProgressStatus = function onUpdateProgressStatus() {
                startLoadInterval();
            };

            const onUpdateProgressPhase = function onUpdateProgressPhase(
                phase
            ) {
                startLoadInterval();

                if (order.getPaymentMethod() !== 'online') {
                    return;
                }

                if (phase === 'confirming') {
                    // We need to ensure that the order has been saved to the server
                    // because the backend may need to do setup in 'confirming'
                    // phase before being able to accept a payment
                    order.save(function() {
                        // Check that the order wasn't unconfirmed in between
                        if (order.getProgressPhase() === 'confirming') {
                            DirectProcessor.confirm();
                        }
                    });
                }
            };

            // override
            DirectProcessor.attachToOrder = function(order) {
                order.bind('updateProgressPhase', onUpdateProgressPhase);
                order.bind('updateProgressStatus', onUpdateProgressStatus);
                order.bind('updatePaymentProcessorError', onError);
            };

            // override
            DirectProcessor.detachFromOrder = function(order) {
                order.unbind('updateProgressPhase', onUpdateProgressPhase);
                order.unbind('updateProgressStatus', onUpdateProgressStatus);
                order.unbind('updatePaymentProcessorError', onError);
            };

            // Override
            DirectProcessor.confirm = function(callback) {
                serverRequest({}, function(transaction) {
                    if (_.isFunction(callback)) {
                        callback(transaction);
                    }
                });
                return DirectProcessor;
            };
        }

        return DirectProcessor;
    },

    /**-------------------------------------------------------------------------
     * Iframe Payment Form
     */
    iframe(properties, order) {
        const IframeProcessor = new BasePaymentProcessor(properties, order);

        const POLL_INTERVAL = 3500;
        let iframe_url = null;

        IframeProcessor.setTransaction = function(transaction) {
            BasePaymentProcessor.prototype.setTransaction.call(
                IframeProcessor,
                transaction
            );
            setIframeUrl(transaction.url);
        };

        IframeProcessor.getIframeUrl = function() {
            return iframe_url;
        };

        function setIframeUrl(provided_url) {
            let url = provided_url;

            // The backend 'hangs on' to iframe urls when the order switches from
            // 'confirming' to 'unconfirmed'. This is a guard to ensure an iframe
            // is never set when 'unconfirmed' to prevent transactions hanging around
            // if the order's total changes and a new transaction is required.
            if (order.getProgressPhase() === 'unconfirmed') {
                url = null;
            }

            if (url !== iframe_url) {
                iframe_url = url;
                order.trigger('updatePaymentProcessorIframeUrl', iframe_url);
            }
        }

        let interval = null;
        function startLoadInterval() {
            if (
                order.getProgressPhase() === 'confirming' &&
                order.getProgressStatus() === 'pending payment'
            ) {
                if (!interval) {
                    interval = setInterval(order.load, POLL_INTERVAL);
                }
            } else {
                stopLoadInterval();
            }
        }

        function stopLoadInterval() {
            clearTimeout(interval);
            interval = null;
        }

        function onUpdateProgressPhase() {
            if (order.getProgressPhase() === 'unconfirmed') {
                setIframeUrl(null);
            }
            startLoadInterval();
        }

        // Check payment status at an interval when status is 'pending payment'
        // as relying on the iframe load event isn't reliable
        IframeProcessor.attachToOrder = function(order) {
            startLoadInterval();
            order.bind({
                updateProgressStatus: startLoadInterval,
                updateProgressPhase: onUpdateProgressPhase,
            });
        };

        IframeProcessor.detachFromOrder = function(order) {
            stopLoadInterval();
            order.unbind({
                updateProcessStatus: startLoadInterval,
                updateProgressPhase: onUpdateProgressPhase,
            });
        };

        return IframeProcessor;
    },

    /**--------------------------------------------------------------------------
     * Redirect Payment Form
     */
    redirect(properties, order) {
        const RedirectProcessor = new BasePaymentProcessor(properties, order);

        let url = null; // Redirect url

        RedirectProcessor.setTransaction = function(transaction) {
            BasePaymentProcessor.prototype.setTransaction.call(
                RedirectProcessor,
                transaction
            );
            setUrl(transaction.url);
        };

        RedirectProcessor.getUrl = function() {
            return url;
        };

        function setUrl(new_url) {
            if (new_url === url) {
                return;
            }
            order.trigger('updatePaymentProcessorRedirectUrl', (url = new_url));
        }

        const POLL_INTERVAL = 5000;
        let interval = null;

        function startLoadInterval() {
            if (
                order.getProgressPhase() === 'confirming' &&
                order.getProgressStatus() === 'pending payment'
            ) {
                if (!interval) {
                    interval = setInterval(order.load, POLL_INTERVAL);
                }
            } else {
                stopLoadInterval();
            }
        }

        function stopLoadInterval() {
            clearTimeout(interval);
            interval = null;
        }

        function onUpdateProgressPhase(phase) {
            startLoadInterval();
            if (phase === 'unconfirmed') {
                url = null;
            }
        }

        RedirectProcessor.attachToOrder = function(order) {
            startLoadInterval();
            order.bind({
                updateProgressStatus: startLoadInterval,
                updateProgressPhase: onUpdateProgressPhase,
            });
        };

        RedirectProcessor.detachFromOrder = function(order) {
            stopLoadInterval();
            order.unbind({
                updateProgressStatus: startLoadInterval,
                updateProgressPhase: onUpdateProgressPhase,
            });
        };

        return RedirectProcessor;
    },
});
